// Word Bloom — endless letter-wheel puzzle (Word Cookies / Zen Word style).
//
// Levels are generated on the fly from a curated lexicon, so the game never
// runs out: each level picks a "letter set" (5-7 distinct letters) and asks
// you to find every common English word hiding inside it.
//
// Level numbering is deterministic: level 42 is the same board for everyone,
// forever. Each pass through a letter-set pool is a fresh seeded permutation,
// so boards keep arriving in a new order once a pool is exhausted.

import { LEXICON, LEXICON_SET, BASES_5, BASES_6, BASES_7 } from "@/data/word-bloom-lexicon";
import { QUOTES, type Quote } from "@/data/quotes";

export interface BloomLevel {
  level: number;
  /** The letters shown on the wheel, already shuffled for this level. */
  letters: string[];
  /** Every accepted word, shortest first. */
  answers: string[];
  /** The word that uses every letter on the wheel (if one exists). */
  pangram?: string;
  /** A famous quote that contains one of the answers — the level reward. */
  reward: { quote: Quote; word: string };
}

/** Deterministic 32-bit PRNG so a given level is identical for every player. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const rnd = mulberry32(seed + 1);
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** How many letters the wheel shows at a given level. */
export function lettersForLevel(level: number): 5 | 6 | 7 {
  if (level <= 8) return 5;
  if (level <= 20) return 6;
  // From level 21 the board alternates 6 / 7 / 7 so long boards stay special.
  return ((level - 21) % 3 === 0 ? 6 : 7) as 6 | 7;
}

function poolFor(size: 5 | 6 | 7): string[] {
  return size === 5 ? BASES_5 : size === 6 ? BASES_6 : BASES_7;
}

/** Index of this level inside its own size group (1-based). */
function groupIndex(level: number, size: number): number {
  let n = 0;
  for (let l = 1; l <= level; l++) if (lettersForLevel(l) === size) n++;
  return n;
}

function isSubset(word: string, base: string): boolean {
  const pool = base.split("");
  for (const ch of word) {
    const i = pool.indexOf(ch);
    if (i < 0) return false;
    pool.splice(i, 1);
  }
  return true;
}

/**
 * Words we never put on a board: slurs, crude terms, and a few first names
 * that slipped through the word-frequency lists.
 */
export const BLOCKED = new Set([
  "abo", "coon", "dago", "gook", "jap", "kike", "mick", "paki", "spic", "wog", "wop", "yid",
  "fag", "fags", "slut", "slag", "hore", "damn", "arse", "turd", "poop", "piss", "crap",
  "jacob", "jacobs", "james", "jane", "jean", "joan", "john", "jose", "juan", "maria",
  "mario", "marie", "peter", "paul", "sarah", "simon", "susan", "nadia", "diana", "linda",
  "adam", "alan", "alex", "amy", "ana", "andy", "ann", "anna", "anne", "bob", "carl",
  "dan", "dave", "don", "eric", "gary", "ian", "jim", "joe", "ken", "leo", "lisa", "mark",
  "mary", "max", "mike", "nick", "pam", "pat", "ray", "rob", "ron", "roy", "sam", "tim",
  "tom", "usa", "uk", "ltd", "inc",
]);

/** Every lexicon word that can be spelled from `base`. */
export function answersFor(base: string): string[] {
  const found = LEXICON.filter(
    (w) =>
      w.length >= 3 && w.length <= base.length && !BLOCKED.has(w) && isSubset(w, base),
  );
  return found.sort((a, b) => a.length - b.length || a.localeCompare(b));
}

/**
 * Reward quotes. We never hand out a random unrelated quote: first we look
 * for a quote containing the longest answer, then — failing that — the quote
 * that shares the most words with this board. A quote with no connection to
 * the level only appears in the rare case that nothing in QUOTES matches.
 */
function rewardFor(answers: string[], level: number): { quote: Quote; word: string } {
  // Prefer the longest answer that actually appears in a famous quotation.
  const candidates = [...answers].reverse();
  for (const w of candidates) {
    if (w.length < 4) break;
    const re = new RegExp(`\\b${w}\\b`, "i");
    const matches = QUOTES.filter((q) => re.test(q.text));
    if (matches.length) {
      const rnd = mulberry32(level * 31 + w.length);
      return { quote: matches[Math.floor(rnd() * matches.length)], word: w };
    }
  }
  // Fallback: the quote that shares the most words with this board.
  const words = new Set(answers.filter((w) => w.length >= 3));
  let bestQuote: Quote | null = null;
  let bestHits = 0;
  let bestWord = "";
  for (const q of QUOTES) {
    const tokens = new Set(q.text.toLowerCase().match(/[a-z]+/g) ?? []);
    let hits = 0;
    let hitWord = "";
    for (const w of words) {
      if (tokens.has(w)) {
        hits++;
        if (!hitWord) hitWord = w;
      }
    }
    if (hits > bestHits) {
      bestHits = hits;
      bestQuote = q;
      bestWord = hitWord;
    }
  }
  if (bestQuote) return { quote: bestQuote, word: bestWord };
  const rnd = mulberry32(level * 977);
  return { quote: QUOTES[Math.floor(rnd() * QUOTES.length)], word: "" };
}

/** Build (or rebuild) any level number. Deterministic. */
export function buildLevel(level: number): BloomLevel {
  const size = lettersForLevel(level);
  const pool = poolFor(size);
  const gi = groupIndex(level, size) - 1;
  const pass = Math.floor(gi / pool.length);
  const permutation = seededShuffle(pool, pass * 7919 + size);

  // Skip any letter set left too thin by the blocked-word filter.
  let base = permutation[gi % pool.length];
  let answers = answersFor(base);
  for (let step = 1; answers.length < 4 && step <= pool.length; step++) {
    base = permutation[(gi + step) % pool.length];
    answers = answersFor(base);
  }

  const pangram = answers.find((w) => w.length === base.length);
  return {
    level,
    letters: seededShuffle(base.split(""), level * 13 + 5),
    answers,
    pangram,
    reward: rewardFor(answers, level),
  };
}

/** Is this a real word in the game's dictionary? */
export function isWord(word: string): boolean {
  return LEXICON_SET.has(word.toLowerCase());
}

/** Can this word be spelled using the letters currently on the wheel? */
export function isFormable(word: string, letters: string[]): boolean {
  const w = word.toLowerCase();
  if (w.length < 3 || w.length > letters.length || BLOCKED.has(w)) return false;
  return isSubset(w, letters.join(""));
}

/** Points for a bonus word — real words off the list pay about half. */
export function bonusScore(word: string): number {
  return Math.max(5, Math.round(wordScore(word) / 2));
}

/** Points for a found word — longer words pay exponentially better. */
export function wordScore(word: string): number {
  const base = { 3: 10, 4: 20, 5: 35, 6: 55, 7: 80, 8: 110 } as Record<number, number>;
  return base[word.length] ?? 10;
}

/** Stars for a completed level: clean runs earn three. */
export function levelStars(hintsUsed: number): number {
  if (hintsUsed === 0) return 3;
  if (hintsUsed <= 2) return 2;
  return 1;
}

export const TOTAL_BOARDS = BASES_5.length + BASES_6.length + BASES_7.length;
